import type { FieldMeta, Resource } from '@tnn/core'
import { z } from 'zod'

/**
 * Signatures (ลายเซ็น): everyone keeps one of their own — drawn on the screen or a photo of it on paper, cleaned — from
 * their job application on, changed in Thananan OS. A document is signed on its lines, each by whoever it names: with
 * one's own kept signature or drawn there and then, in an app, on a link sent to someone without an account, or on a
 * driver's phone at the door. Each signature keeps who, when, from where and a hash of what the document said.
 */

/**
 * `stored`: one's kept signature; `drawn`: on the screen there and then; `photo`: a photo of it on paper, cleaned;
 * `paper`: signed by hand on the printout, the image a photo of that page
 */
export const signatureMethods = ['stored', 'drawn', 'photo', 'paper'] as const
export type SignatureMethod = (typeof signatureMethods)[number]
/** What a kept signature was made by (older ones were drawn) */
export const keptSignatureMethods = ['drawn', 'photo'] as const
/**
 * How a signature is made to be kept as one's own (บัญชีของฉัน, the job application, ticked while signing): a photo of
 * it on paper — a finger on a phone draws a scrawl that will not pass for one's signature on what one signs later. A
 * document may still be signed on the screen there and then
 */
export const keepableSignatureMethods = ['photo'] as const
const photographedSignature = 'ลายเซ็นที่เก็บไว้ต้องเป็นรูปถ่ายลายเซ็นบนกระดาษ'

/** Whether a signature made this way may be kept as one's own */
export const keepable = (method: unknown) => (keepableSignatureMethods as readonly unknown[]).includes(method)

/**
 * `app`: signed in; `link`: a link sent to someone without an account; `device`: on a staff member's phone (a
 * delivery); `standing`: the company's authorized signer's, printed on what the company issues as its settings say
 */
export const signatureVias = ['app', 'link', 'device', 'standing'] as const
export type SignatureVia = (typeof signatureVias)[number]

/**
 * How the line someone outside the company signs is signed, in the document's signing box: `here` on the screen in
 * front of staff (theirs, or their portable screen); `link` sent to them, signed on their own phone; `paper` signed by
 * hand on the printout, a photo of it kept
 */
export const signingChannels = ['here', 'link', 'paper'] as const
export type SigningChannel = (typeof signingChannels)[number]
/** The customer is seldom there when an accounting document goes out: a link first */
const sent = ['link', 'here', 'paper'] as const satisfies readonly SigningChannel[]
/** The sender is at the counter as the bill is made: here first */
const atCounter = ['here', 'link', 'paper'] as const satisfies readonly SigningChannel[]

/**
 * The lines each kind of document has, by resource key: who signs on each, and how it is signed in the document's
 * signing box (one line a document), the usual way first. None: a step of the work signs it — issuing it, handing the
 * goods over, asking for the advance — which is its proof, so it is signed there only
 */
export const signatureLines = {
  /** The employee agrees to have it taken back from their pay */
  'payroll.advance': { employee: [] },
  /** The company's authorized signer; the customer who received it */
  'accounting.invoice': { authorized: [], customer: sent },
  /** Who billed (issued it); the customer who received the bill */
  'accounting.billingNote': { biller: [], customer: sent },
  /** Who received the money (issued it); the customer who paid */
  'accounting.payment': { receiver: [], customer: sent },
  /** Who received the goods at the door, on the driver's phone */
  'tms.shipment': { consignee: [] },
  /**
   * The sender, accepting the shipping terms the bill keeps; who received the goods, handed over at the counter or at
   * the door on the delivery run's driver's phone
   */
  'tms.waybill': { sender: atCounter, consignee: [] },
} as const satisfies Record<string, Record<string, readonly SigningChannel[]>>
export type SignedSubject = keyof typeof signatureLines

type ChannelsOf<S extends SignedSubject> = (typeof signatureLines)[S][keyof (typeof signatureLines)[S]]
type Each<T> = T extends readonly (infer C)[] ? C : never
/** Documents with a signing box */
export type BoxSubject = { [S in SignedSubject]: ChannelsOf<S> extends readonly [] ? never : S }[SignedSubject]
/** Documents whose box line may be signed on a link */
export type LinkSignedSubject = { [S in SignedSubject]: 'link' extends Each<ChannelsOf<S>> ? S : never }[SignedSubject]

/** A document's signing box: the line on it someone outside signs, and how; null for a document with none */
export function boxOf(subject: string): { role: string; channels: readonly SigningChannel[] } | null {
  if (!Object.hasOwn(signatureLines, subject)) return null
  const lines: Record<string, readonly SigningChannel[]> = signatureLines[subject as SignedSubject]
  const found = Object.entries(lines).find(([, channels]) => channels.length)
  return found ? { role: found[0], channels: found[1] } : null
}

/** Documents whose box line may be signed on a link: to receive or accept it, then keep it */
export const linkSignedSubjects = (Object.keys(signatureLines) as SignedSubject[]).filter((subject) =>
  boxOf(subject)?.channels.includes('link'),
) as LinkSignedSubject[]
/**
 * What has a document's box line signed, or sends its link: a bill's branches change it (`update`); an issued
 * accounting document changes no more, so whoever may give it out (`print`)
 */
export const boxActions: Record<BoxSubject, 'update' | 'print'> = {
  'accounting.invoice': 'print',
  'accounting.billingNote': 'print',
  'accounting.payment': 'print',
  'tms.waybill': 'update',
}
/** How long a signing link works: to sign from when it is sent, to keep the document from when it is signed */
export const SIGN_LINK_DAYS = 30

/** A signing link as the apps show it: `open` to sign, `signed`, or `expired` */
export interface SignLinkInfo {
  id: string
  /** The line it signs */
  role: string
  /** The secret in the link: only to whoever may send it */
  token: string | null
  status: 'open' | 'signed' | 'expired'
  expiresAt: string
  viewedAt: string | null
  signedAt: string | null
}

/** Who signs a box line, as printed under it: typed on a link by them, or at the counter by staff */
export const boxSignerInput = z.object({
  name: z.string().trim().min(1, 'กรุณากรอกชื่อผู้เซ็น').max(120),
  title: z.string().trim().max(100).nullish(),
})

/**
 * Signing a box line in front of staff `{ role?, name, title?, signature }`: drawn on their screen or portable screen
 * (a PNG they uploaded), or a photo of it on paper, cleaned. No role: the document's box line
 */
export const signHereInput = boxSignerInput.extend({
  role: z.string().min(1).optional(),
  signature: z.object({ fileId: z.string().min(1), method: z.enum(keptSignatureMethods) }, { error: 'กรุณาเซ็นชื่อ' }),
})
/** A box line signed by hand on the printout `{ role?, name, title?, photoFileId }`: a photo of that page, the proof */
export const signedOnPaperInput = boxSignerInput.extend({
  role: z.string().min(1).optional(),
  photoFileId: z.string({ error: 'ถ่ายรูปเอกสารที่เซ็นแล้ว' }).min(1, 'ถ่ายรูปเอกสารที่เซ็นแล้ว'),
})

/**
 * What a signature's hash covers of a document: what it says — its number and what was written on it, its form's
 * fields (not the links to the address book, which merging contacts moves) — and not what has happened to it since
 * (its status, where it is, what is paid), so a bill moving on is not a change to what was signed
 */
export function signedContent(resource: Resource, record: Record<string, unknown>): Record<string, unknown> {
  const written = Object.keys(resource.schema.shape).filter((name) => resource.field(name)?.ref?.resource !== 'tms.contact')
  return Object.fromEntries(['number', ...written].map((name) => [name, record[name] ?? null]))
}

/** Where a document's box line stands in its list: `signed`, or `unsigned` (a link sent and not signed yet too) */
export const signingStatuses = ['signed', 'unsigned'] as const
export type SigningStatus = (typeof signingStatuses)[number]
/** A document's box line as a list column and filter (`signing`, which its store adds and filters on) */
export const signingField = {
  readOnly: true,
  type: 'select',
  options: signingStatuses,
  optionLabels: 'signature.signing',
  tones: { signed: 'success', unsigned: 'warning' },
  filter: true,
  list: { width: 150, priority: 3 },
  form: false,
  import: false,
} as const satisfies FieldMeta

/** How a signature is given: one's kept one, or an image just uploaded (a PNG by the signer: `POST /api/files`) */
export const signatureInput = z.union(
  [z.object({ stored: z.literal(true) }), z.object({ fileId: z.string().min(1), method: z.enum(keptSignatureMethods) })],
  { error: 'กรุณาเซ็นชื่อ' },
)
export type SignatureInput = z.infer<typeof signatureInput>

/** Setting one's kept signature: the PNG uploaded and how it was made (a photo: `keepableSignatureMethods`) */
export const keptSignatureInput = z.object({
  fileId: z.string().min(1),
  method: z.enum(keepableSignatureMethods, { error: photographedSignature }),
})
export type KeptSignatureInput = z.infer<typeof keptSignatureInput>

/** A signature as the apps show it */
export interface Signature {
  id: string
  subject: string
  recordId: string
  role: string
  userId: string | null
  name: string
  title: string | null
  fileId: string
  method: SignatureMethod
  via: SignatureVia
  photoFileId: string | null
  signedAt: string
  /** What the document says (`signedContent`) changed after it was signed: "แก้หลังเซ็น" beside it */
  changed: boolean
}

/** One's kept signature */
export interface KeptSignature {
  fileId: string
  method: (typeof keptSignatureMethods)[number]
  updatedAt: string
}
